/**
 * Daten-Hooks für Schichttausch (Phase E, Kapitel 6) — bewusst NICHT über
 * Dexie/Outbox wie die übrigen Schicht-Daten: Tausch-Angebote sind
 * kurzlebige, live zu koordinierende Vorgänge zwischen mehreren Personen
 * (Push-getrieben), kein offline-editierbarer eigener Datensatz. Reines
 * React-Query mit kurzer Polling-Zeit reicht hier aus.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { shiftSwapsApi, type CreateSwapInput } from '../lib/shiftSwaps';
import { db } from '../db/schema';

const SWAPS_KEY = ['shift-swaps'];

export function useShiftSwaps() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: SWAPS_KEY,
    queryFn: () => shiftSwapsApi.list(token!),
    enabled: !!token,
    // Kurzes Polling, damit angenommene/abgelehnte Angebote zeitnah
    // verschwinden, auch ohne dass die Push-Notification angetippt wird.
    refetchInterval: 60_000,
  });
}

export function useCreateSwap() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSwapInput) => shiftSwapsApi.create(token!, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SWAPS_KEY }),
  });
}

export function useAcceptSwap() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shiftSwapsApi.accept(token!, id),
    onSuccess: async (res) => {
      // Die übernommene Schicht direkt in den lokalen Cache schreiben, damit
      // sie sofort im eigenen Kalender auftaucht (kein Warten auf den
      // nächsten periodischen Pull-Sync). Für die abgebende Person (A)
      // verschwindet sie dort automatisch beim nächsten Sync, da die
      // userId-basierte Abfrage sie dann nicht mehr matcht.
      await db.shifts.put({ ...res.shift, pending: false });
      qc.invalidateQueries({ queryKey: SWAPS_KEY });
    },
  });
}

export function useRejectSwap() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shiftSwapsApi.reject(token!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SWAPS_KEY }),
  });
}

export function useCancelSwap() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shiftSwapsApi.cancel(token!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SWAPS_KEY }),
  });
}
